import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { addDoc, collection } from 'firebase/firestore';
import { db } from '../../firebase';
import toast from 'react-hot-toast';

const AdvancedBookingModal = ({ isOpen, onClose, photographer, initialService = null, onFallbackToBasic }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPackage, setSelectedPackage] = useState(initialService);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [clientInfo, setClientInfo] = useState({
    name: '',
    email: '',
    phone: '',
    partnerName: '', // For weddings
    eventDate: '', // For weddings/events
    venue: '',
    eventType: '',
    guestCount: '',
    specialRequests: ''
  });

  // Steps configuration
  const steps = [
    { id: 1, title: 'Select Service', description: 'Choose your photography package' },
    { id: 2, title: 'Schedule', description: 'Pick your preferred date and time' },
    { id: 3, title: 'Details', description: 'Tell us about your event' },
    { id: 4, title: 'Review', description: 'Confirm your booking' }
  ];

  // Find active service packages
  const activePackages = photographer.servicePackages?.filter(pkg => pkg.isActive !== false && pkg.name && pkg.basePrice) || [];

  // Fallback: if no active packages, close modal and show a toast, then call onFallbackToBasic
  useEffect(() => {
    if (isOpen && activePackages.length === 0) {
      toast.error('No service packages available. Please use the regular booking form.');
      onClose();
      if (onFallbackToBasic) {
        setTimeout(() => onFallbackToBasic(), 200); // slight delay to avoid modal overlap
      }
    }
    // eslint-disable-next-line
  }, [isOpen]);

  useEffect(() => {
    if (selectedDate && selectedPackage) {
      fetchAvailableSlots();
    }
  }, [selectedDate, selectedPackage]);

  const fetchAvailableSlots = async () => {
    // Mock implementation - in real app, this would check photographer's availability
    const mockSlots = [
      '09:00', '10:00', '11:00', '13:00', '14:00', '15:00', '16:00', '17:00'
    ];
    setAvailableSlots(mockSlots);
  };

  const calculateTotal = () => {
    let total = selectedPackage?.basePrice || 0;
    selectedAddOns.forEach(addOn => {
      total += addOn.price;
    });
    return total;
  };

  const handleSubmit = async () => {
    if (!clientInfo.name || !clientInfo.email) {
      toast.error('Please fill in your name and email');
      return;
    }

    setIsSubmitting(true);
    try {
      const bookingData = {
        photographerId: photographer.id,
        photographerName: photographer.name,
        clientInfo,
        servicePackage: selectedPackage,
        selectedAddOns,
        scheduledDate: selectedDate,
        scheduledTime: selectedTime,
        totalAmount: calculateTotal(),
        depositAmount: Math.round(calculateTotal() * 0.5), // 50% deposit
        status: 'pending',
        paymentStatus: 'unpaid',
        createdAt: new Date(),
        timeline: [{
          date: new Date().toISOString(),
          event: 'booking_created',
          details: 'Initial booking request submitted'
        }]
      };

      await addDoc(collection(db, 'bookings'), bookingData);
      toast.success('Booking request sent successfully!');
      onClose();
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error('Failed to submit booking request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Select a Service Package</h3>
        <div className="grid gap-4">
          {photographer.servicePackages?.filter(pkg => pkg.isActive !== false).map((pkg) => (
            <div
              key={pkg.id}
              onClick={() => setSelectedPackage(pkg)}
              className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                selectedPackage?.id === pkg.id
                  ? 'border-[#E6A15E] bg-[#E6A15E]/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h4 className="font-semibold text-lg">{pkg.name}</h4>
                  <p className="text-gray-600 text-sm mt-1">{pkg.description}</p>
                  {pkg.duration && (
                    <p className="text-gray-500 text-sm mt-1">Duration: {pkg.duration}</p>
                  )}
                  {pkg.includedServices && pkg.includedServices.length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm font-medium text-gray-700">Includes:</p>
                      <ul className="text-sm text-gray-600 mt-1">
                        {pkg.includedServices.slice(0, 3).map((service, idx) => (
                          <li key={idx}>• {service}</li>
                        ))}
                        {pkg.includedServices.length > 3 && (
                          <li>• +{pkg.includedServices.length - 3} more features</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-[#E6A15E]">
                    {pkg.currency || 'USD'} {pkg.basePrice?.toLocaleString()}
                  </div>
                  {pkg.popular && (
                    <span className="inline-block px-2 py-1 bg-[#E6A15E] text-black text-xs font-medium rounded-full mt-1">
                      Popular
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Select Date & Time</h3>
        <div className="grid gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Preferred Date</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#E6A15E]"
            />
          </div>
          {selectedDate && (
            <div>
              <label className="block text-sm font-medium mb-2">Preferred Time</label>
              <div className="grid grid-cols-4 gap-2">
                {availableSlots.map((slot) => (
                  <button
                    key={slot}
                    onClick={() => setSelectedTime(slot)}
                    className={`p-3 text-sm rounded-lg border transition-colors ${
                      selectedTime === slot
                        ? 'border-[#E6A15E] bg-[#E6A15E] text-black'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {slot}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Event Details</h3>
        <div className="grid gap-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Your Name *</label>
              <input
                type="text"
                value={clientInfo.name}
                onChange={(e) => setClientInfo(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#E6A15E]"
                placeholder="Full name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Email *</label>
              <input
                type="email"
                value={clientInfo.email}
                onChange={(e) => setClientInfo(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#E6A15E]"
                placeholder="email@example.com"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Phone</label>
            <input
              type="tel"
              value={clientInfo.phone}
              onChange={(e) => setClientInfo(prev => ({ ...prev, phone: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#E6A15E]"
              placeholder="Phone number"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Event Type</label>
            <select
              value={clientInfo.eventType}
              onChange={(e) => setClientInfo(prev => ({ ...prev, eventType: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#E6A15E]"
            >
              <option value="">Select event type</option>
              <option value="wedding">Wedding</option>
              <option value="portrait">Portrait Session</option>
              <option value="commercial">Commercial</option>
              <option value="event">Event</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Venue/Location</label>
            <input
              type="text"
              value={clientInfo.venue}
              onChange={(e) => setClientInfo(prev => ({ ...prev, venue: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#E6A15E]"
              placeholder="Venue or location"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Special Requests</label>
            <textarea
              value={clientInfo.specialRequests}
              onChange={(e) => setClientInfo(prev => ({ ...prev, specialRequests: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#E6A15E] h-24"
              placeholder="Any special requests or additional information..."
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Review Your Booking</h3>
        <div className="bg-gray-50 rounded-lg p-6 space-y-4">
          <div>
            <h4 className="font-medium">Selected Package</h4>
            <p className="text-gray-600">{selectedPackage?.name} - {selectedPackage?.currency || 'USD'} {selectedPackage?.basePrice?.toLocaleString()}</p>
          </div>
          <div>
            <h4 className="font-medium">Scheduled Date & Time</h4>
            <p className="text-gray-600">{selectedDate} at {selectedTime}</p>
          </div>
          <div>
            <h4 className="font-medium">Client Information</h4>
            <p className="text-gray-600">{clientInfo.name} ({clientInfo.email})</p>
            {clientInfo.phone && <p className="text-gray-600">{clientInfo.phone}</p>}
          </div>
          <div>
            <h4 className="font-medium">Total Amount</h4>
            <p className="text-2xl font-bold text-[#E6A15E]">{selectedPackage?.currency || 'USD'} {calculateTotal().toLocaleString()}</p>
            <p className="text-sm text-gray-500">50% deposit required: {selectedPackage?.currency || 'USD'} {Math.round(calculateTotal() * 0.5).toLocaleString()}</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      default: return renderStep1();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h1 className="text-xl font-semibold">Book with {photographer.name}</h1>
            <p className="text-gray-600">{photographer.professionalTitle}</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
          >
            ×
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  currentStep >= step.id
                    ? 'bg-[#E6A15E] text-black'
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {step.id}
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-16 h-1 mx-2 ${
                    currentStep > step.id ? 'bg-[#E6A15E]' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="mt-2 text-center">
            <h3 className="font-medium">{steps[currentStep - 1].title}</h3>
            <p className="text-sm text-gray-600">{steps[currentStep - 1].description}</p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {renderCurrentStep()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <button
            onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
            disabled={currentStep === 1}
            className="px-6 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          
          <div className="flex gap-3">
            {currentStep < 4 ? (
              <button
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={
                  (currentStep === 1 && !selectedPackage) ||
                  (currentStep === 2 && (!selectedDate || !selectedTime)) ||
                  (currentStep === 3 && (!clientInfo.name || !clientInfo.email))
                }
                className="px-6 py-3 bg-[#E6A15E] text-black rounded-lg hover:bg-[#E6A15E]/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-6 py-3 bg-[#E6A15E] text-black rounded-lg hover:bg-[#E6A15E]/90 disabled:opacity-50"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Booking'}
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdvancedBookingModal; 